import React, { useRef, useState, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Badge,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { buildSections } from './buildSections';
import { useOverflowTabs } from './useOverflowTabs';
import { ProfileSubnavProps, SectionKey } from './types';
import ProfileSubnavMobile from './ProfileSubnavMobile';

/**
 * Desktop/Tablet profile navigation with overflow "More" menu
 *
 * Automatically switches to mobile variant on small screens
 * Drop-in replacement for ProfileTabs component
 */
export const ProfileSubnav: React.FC<ProfileSubnavProps> = ({
  personId,
  activeKey,
  counts = {},
  flags = {},
  perms = {},
  onNavigate,
  sticky = false,
  variant = 'auto',
  routePrefix,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Build all sections based on flags/perms/counts
  const allSections = useMemo(
    () => buildSections(personId, counts, flags, perms, routePrefix),
    [personId, counts, flags, perms, routePrefix]
  );

  // Calculate visible vs overflow tabs
  const { visible, overflow } = useOverflowTabs({
    containerRef,
    sections: allSections,
    moreButtonWidth: 60, // Icon button with border is ~48px + margins
  });

  // Determine if we should use mobile variant
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));
  const useMobile = variant === 'mobile' || (variant === 'auto' && isMobileScreen);

  // Delegate to mobile component if needed
  if (useMobile) {
    return (
      <ProfileSubnavMobile
        personId={personId}
        activeKey={activeKey}
        counts={counts}
        flags={flags}
        perms={perms}
        onNavigate={onNavigate}
        routePrefix={routePrefix}
      />
    );
  }

  // Handle "More" menu
  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setAnchorEl(null);
  };

  const handleOverflowItemClick = (key: SectionKey) => {
    handleMoreClose();
    onNavigate(key);
  };

  // Handle tab change from Tabs component
  const handleTabChange = (_: React.SyntheticEvent, newValue: SectionKey) => {
    onNavigate(newValue);
  };

  return (
    <Box
      ref={containerRef}
      role="navigation"
      aria-label="Profile sections"
      sx={{
        position: sticky ? 'sticky' : 'static',
        top: sticky ? 0 : undefined,
        zIndex: sticky ? 5 : undefined,
        backgroundColor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* Visible Tabs */}
      <Tabs
        value={activeKey}
        onChange={handleTabChange}
        variant="standard"
        scrollButtons={false}
        sx={{
          flex: 1,
          minWidth: 0,
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        }}
        aria-label="Primary profile sections"
      >
        {visible.map((section) => (
          <Tab
            key={section.key}
            value={section.key}
            role="tab"
            aria-label={section.ariaLabel ?? section.label}
            aria-selected={section.key === activeKey}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {section.badgeCount !== undefined && section.badgeCount > 0 ? (
                  <Badge badgeContent={section.badgeCount} color="primary" max={99}>
                    {section.icon}
                  </Badge>
                ) : (
                  section.icon
                )}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.875rem',
                  }}
                >
                  {section.label}
                </Box>
              </Box>
            }
            sx={{
              textTransform: 'none',
              minHeight: 64,
              px: 2,
              '&.Mui-selected': {
                color: 'primary.main',
              },
            }}
          />
        ))}
      </Tabs>

      {/* "More" Button */}
      {overflow.length > 0 && (
        <IconButton
          onClick={handleMoreClick}
          aria-label="More sections"
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
          sx={{
            mr: 1,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <MoreHorizIcon />
        </IconButton>
      )}

      {/* "More" Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMoreClose}
        MenuListProps={{
          role: 'menu',
          'aria-label': 'Additional profile sections',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {overflow.map((section) => (
          <MenuItem
            key={section.key}
            onClick={() => handleOverflowItemClick(section.key)}
            selected={section.key === activeKey}
            role="menuitem"
          >
            {section.icon && <ListItemIcon>{section.icon}</ListItemIcon>}
            <ListItemText primary={section.label} />
            {section.badgeCount !== undefined && section.badgeCount > 0 && (
              <Badge
                badgeContent={section.badgeCount}
                color="primary"
                max={99}
                sx={{ ml: 2 }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default ProfileSubnav;
